import '@digital-realty/ix-icon-button/ix-icon-button.js';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';

import FileInfo from './internal/file-info.js';
import IxFileChipStyles from './styles/ix-file-chip-styles.js';
import UploaderFile from './internal/uploader-file.js';
import formatFileSize from './internal/format-file-size.js';
import { trash } from './assets/iconset.js';

export class IxFileChip extends LitElement {
  static readonly styles = [IxFileChipStyles];

  @property({ type: Object }) file?: UploaderFile;

  @property({ type: Boolean }) deletable: boolean = true;

  /** 1-based position of this chip within its parent list. */
  @property({ type: Number }) position: number = 0;

  /** Total number of chips in the parent list. */
  @property({ type: Number }) total: number = 0;

  /**
   * Upload state describing whether the file has been accepted by the
   * uploader. Included in the chip's accessible name so screen-reader
   * users hear the status when the chip receives focus.
   */
  @property() uploadState: 'uploaded' | 'uploading' | 'error' = 'uploaded';

  connectedCallback(): void {
    super.connectedCallback();

    // Expose the chip host as a listitem so that the surrounding
    // `<ul role="list">` reports valid list structure to assistive
    // technology (and to axe). The actual `<li>` inside the shadow DOM
    // does not participate in the outer list because of the shadow
    // boundary, so we rely on the host role instead.
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'listitem');
    }
  }

  updated(changedProperties: Map<string | number | symbol, unknown>): void {
    // Only touch attributes when position/total actually changed to
    // avoid triggering unnecessary re-render cycles.
    if (!changedProperties.has('position') && !changedProperties.has('total')) {
      return;
    }

    // Keep aria-posinset/aria-setsize in sync with position/total so the
    // browser exposes a consistent list position across screen readers.
    if (this.position > 0 && this.total > 0) {
      this.setAttribute('aria-posinset', String(this.position));
      this.setAttribute('aria-setsize', String(this.total));
    } else {
      this.removeAttribute('aria-posinset');
      this.removeAttribute('aria-setsize');
    }
  }

  /**
   * Move focus to the delete (remove) button on this chip.
   * Falls back to the file name button when the delete button
   * is not rendered (non-deletable chips).
   */
  public focusDelete(): void {
    const deleteBtn = this.shadowRoot?.querySelector(
      'ix-icon-button'
    ) as HTMLElement | null;

    if (deleteBtn) {
      deleteBtn.focus();
      return;
    }

    this.focusName();
  }

  /** Move focus to the file name button on this chip. */
  public focusName(): void {
    const nameBtn = this.shadowRoot?.querySelector(
      '.file-chip__name'
    ) as HTMLElement | null;
    nameBtn?.focus();
  }

  private onClick(): void {
    const event = new CustomEvent<FileInfo>('on-file-click', {
      detail: {
        file: this.file,
      },
    });

    this.dispatchEvent(event);
  }

  private onChipKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.onClick();
    }
  }

  private onDelete(): void {
    if (this.deletable) {
      const event = new CustomEvent<FileInfo>('on-file-delete', {
        detail: {
          file: this.file,
        },
      });

      this.dispatchEvent(event);
    }
  }

  private getUploadStateLabel(): string {
    switch (this.uploadState) {
      case 'uploading':
        return 'uploading';
      case 'error':
        return 'upload failed';
      default:
        return 'uploaded';
    }
  }

  protected render(): TemplateResult<1> {
    const fileName = this.file?.name ?? '';
    const size = this.file?.size ?? 0;
    const sizeLabel = size > 0 ? formatFileSize(size) : '';
    const stateLabel = this.getUploadStateLabel();

    // Compose a descriptive accessible name that exposes the file name,
    // size, and upload state on focus. List position is conveyed via
    // aria-posinset/aria-setsize on the host so we deliberately avoid
    // adding a "position of total" fragment here; that would otherwise
    // be double-announced against the file name button and be
    // inconsistent between file and Remove buttons for duplicates.
    const fileLabelParts = [`File ${fileName}`];
    if (sizeLabel) fileLabelParts.push(sizeLabel);
    fileLabelParts.push(stateLabel);
    const fileLabel = fileLabelParts.join(', ');

    const removeLabelParts = [`Remove file ${fileName}`];
    if (sizeLabel) removeLabelParts.push(sizeLabel);
    const removeLabel = removeLabelParts.join(', ');

    return html`<div class="file-chip">
      <span
        class="file-chip__name"
        role="button"
        tabindex="0"
        aria-label="${fileLabel}"
        @click=${this.onClick}
        @keydown=${this.onChipKeyDown}
      >
        ${fileName}
      </span>

      ${this.deletable
        ? html`<ix-icon-button
            @click=${this.onDelete}
            name="delete-button"
            .label=${removeLabel}
            title="${removeLabel}"
          >
            <ix-icon slot="default" class="delete-icon" aria-hidden="true">
              ${trash}
            </ix-icon>
          </ix-icon-button>`
        : nothing}
    </div>`;
  }
}
